/**
 * SportsCourtMonitoringPage — Court calendar redesigned as a PlayServe-style grid.
 * Inspired by: https://playserve.co
 *
 * Layout:
 *   - Rows: time marks (every 30 min from earliest open to latest close)
 *   - Columns: one per active court
 *   - Cells: empty (available), booking (spans multiple rows), closed (outside hours)
 *   - Bookings are color-coded by type/status
 *
 * Mobile: horizontally scrollable — time column sticky on left.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { fmt12, ymdTz, addDaysYmd } from '../utils/sportsUtils';
import BookingDetailBody from '../components/BookingDetailBody';

const STATUS_COLORS = {
  confirmed:             '#00E676',
  awaiting_confirmation: '#FFB300',
  hold:                  '#737373',
  floating:              '#556',
  walk_in:               '#2979FF',
  competition:           '#FF6D00',
  cancelled:             '#3A3A3A',
  declined:              '#FF2D55',
};

const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const CAL_DOT = { green: '#22c55e', yellow: '#eab308', orange: '#f97316', red: '#ef4444' };

/* ── Inline calendar popup for monitoring page ────────────────── */
function MonitoringCalendar({ value, onChange, onClose, todayStr }) {
  const init = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewYear, setViewYear] = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());
  const [calData, setCalData] = useState({});

  useEffect(() => {
    sportsApi.get('/sports/bookings/calendar-summary', { params: { year: viewYear, month: viewMonth + 1 } })
      .then(r => setCalData(r.data || {}))
      .catch(() => setCalData({}));
  }, [viewYear, viewMonth]);

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); };

  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const fmt = d => `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  return (
    <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 30, marginTop: 6, background: '#141414', border: '1px solid #2A2A2A', borderRadius: 12, padding: '14px 12px', width: 280, boxShadow: '0 8px 30px rgba(0,0,0,0.6)' }}
      onClick={e => e.stopPropagation()}>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#737373', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}>
          <ChevronLeft size={14} />
        </button>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'Oswald, sans-serif', letterSpacing: 1.5, textTransform: 'uppercase' }}>
          {MONTHS_FULL[viewMonth]} {viewYear}
        </span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#737373', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}>
          <ChevronRight size={14} />
        </button>
      </div>
      {/* DOW header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 3 }}>
        {DOW_SHORT.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#555', textTransform: 'uppercase', paddingBottom: 5 }}>{d}</div>
        ))}
      </div>
      {/* Days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;
          const dateStr = fmt(day);
          const isSelected = dateStr === value;
          const isToday = dateStr === todayStr;
          const dot = calData[dateStr]?.status;
          const dotColor = dot ? CAL_DOT[dot] : null;
          return (
            <button key={dateStr}
              onClick={() => { onChange(dateStr); onClose(); }}
              style={{
                position: 'relative', height: 34, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: 7, fontSize: 11, fontWeight: isSelected || isToday ? 700 : 400, cursor: 'pointer',
                border: isSelected ? '1.5px solid #E63946' : isToday ? '1px solid #E6394655' : '1px solid transparent',
                background: isSelected ? '#E6394628' : isToday ? '#E6394612' : 'transparent',
                color: isSelected ? '#E63946' : '#ccc',
                outline: 'none',
              }}>
              <span style={{ lineHeight: 1 }}>{day}</span>
              {dotColor && <span style={{ position: 'absolute', bottom: 3, width: 4, height: 4, borderRadius: '50%', background: dotColor }} />}
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid #1E1E1E' }}>
        {[['#22c55e','Open'],['#eab308','Limited'],['#f97316','Few slots'],['#ef4444','Full']].map(([c, l]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: '#555' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: c }} />{l}
          </div>
        ))}
      </div>
    </div>
  );
}

function toMin(t) {
  if (!t) return 0;
  // iOS Safari saves midnight as "00:00" where Chrome uses "24:00".
  // As an END/close marker, "00:00" means end-of-day (1440), never 0 —
  // otherwise bookings ending at midnight vanish from the grid.
  if (t === '00:00' || t === '0:00') return 1440;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/** Format a minute-offset as a short time label (e.g. 420 → "7a", 450 → ":30", 1320 → "10p") */
function fmtTimeMark(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m !== 0) return `:${String(m).padStart(2, '0')}`;
  if (h >= 24) {
    const adjH = h - 24;
    if (adjH === 0) return '12a';
    const ampm = adjH >= 12 ? 'p' : 'a';
    return `${adjH % 12 || 12}${ampm}`;
  }
  const ampm = h >= 12 ? 'p' : 'a';
  return `${h % 12 || 12}${ampm}`;
}

function isCourtOpenAt(court, timeMin) {
  return timeMin >= toMin(court.open_time) && timeMin < toMin(court.close_time);
}

// ── Day-band schedules (mirrors backend sports/booking_engine.py) ──
function effectiveCourt(court, dateStr) {
  const scheds = court?.schedules;
  if (!scheds?.length || !dateStr) return court;
  const wd = (new Date(dateStr + 'T12:00:00').getDay() + 6) % 7; // JS Sun=0 → backend Mon=0
  const s = scheds.find(x => (x.days || []).includes(wd));
  if (!s) return court;
  const eff = { ...court };
  ['open_time', 'close_time', 'rate_type', 'flat_rate', 'rate_bands', 'min_duration_rules', 'min_duration_minutes']
    .forEach(k => {
      const v = s[k];
      if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length)) eff[k] = v;
    });
  return eff;
}

// Latest possible closing across every schedule (for overnight-window checks).
function maxCloseMinOf(court) {
  const closes = [toMin(court.close_time), ...((court.schedules || []).map(s => toMin(s.close_time || court.close_time)))];
  return Math.max(...closes);
}

// Current minutes-of-day in the facility's timezone (0..1439).
function currentMinInTz(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (k) => parseInt(parts.find(p => p.type === k)?.value || '0', 10);
    return (get('hour') % 24) * 60 + get('minute');
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}

function bookingColor(b) {
  if (b.booking_type === 'competition') return STATUS_COLORS.competition;
  if (b.booking_type === 'walk_in' || b.source === 'walk_in') return STATUS_COLORS.walk_in;
  return STATUS_COLORS[b.status] || '#737373';
}

const DOW_LONG = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTH_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtDateLong(ds) {
  const d = new Date(ds + 'T00:00:00');
  return `${DOW_LONG[d.getDay()]}, ${MONTH_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export default function SportsCourtMonitoringPage() {
  // Facility timezone
  const [facilityTz, setFacilityTz] = useState('Asia/Manila');
  const todayInTz = ymdTz(facilityTz);

  const [date, setDate]         = useState(todayInTz);
  const [courts, setCourts]     = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [showCal, setShowCal]   = useState(false);

  useEffect(() => {
    Promise.all([
      sportsApi.get('/sports/courts').catch(() => ({ data: [] })),
      sportsApi.get('/sports/settings').catch(() => ({ data: {} })),
    ]).then(([cr, sr]) => {
      const cs = cr.data || [];
      setCourts(cs);
      const tz = sr.data?.timezone || 'Asia/Manila';
      setFacilityTz(tz);
      // Business-day default: if it's past midnight but a court is still open
      // from yesterday (close_time > 24:00, e.g. "27:00" = 3 AM), open the
      // board on YESTERDAY's grid so in-progress overnight bookings stay visible.
      const act = cs.filter(c => c.active);
      const maxClose = act.length ? Math.max(...act.map(maxCloseMinOf)) : 1440;
      const today = ymdTz(tz);
      const overnight = maxClose > 1440 && currentMinInTz(tz) < maxClose - 1440;
      setDate(overnight ? addDaysYmd(today, -1) : today);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    sportsApi.get('/sports/bookings', { params: { date } })
      .then(r => setBookings(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);

  const changeDate = useCallback((delta) => {
    setDate(prev => addDaysYmd(prev, delta));
  }, []);

  // Close calendar on outside click
  useEffect(() => {
    if (!showCal) return;
    const handler = () => setShowCal(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showCal]);

  // Day-band schedules: resolve each court's hours for the date being viewed.
  const activeCourts = useMemo(() => courts.filter(c => c.active).map(c => effectiveCourt(c, date)), [courts, date]);

  // "Business today": while it's past midnight inside an overnight window
  // (a court closes after 24:00), the operating day is still YESTERDAY's date.
  const maxCloseMin = activeCourts.length ? Math.max(...activeCourts.map(c => toMin(c.close_time))) : 1440;
  const inOvernightWindow = maxCloseMin > 1440 && currentMinInTz(facilityTz) < maxCloseMin - 1440;
  const businessToday = inOvernightWindow ? addDaysYmd(todayInTz, -1) : todayInTz;

  /* ── Time marks: every 30 min from earliest open to latest close ── */
  const timeMarks = useMemo(() => {
    if (!activeCourts.length) return [];
    const startMin = Math.min(...activeCourts.map(c => toMin(c.open_time)));
    const endMin   = Math.max(...activeCourts.map(c => toMin(c.close_time)));
    const marks = [];
    for (let t = startMin; t <= endMin; t += 30) marks.push(t);
    return marks;
  }, [activeCourts]);

  /* ── Grid map: precompute what each cell should render ── */
  const grid = useMemo(() => {
    const g = {};
    const skip = new Set();

    activeCourts.forEach(court => {
      const cBookings = bookings.filter(b =>
        b.court_id === court.id &&
        !['expired', 'cancelled', 'declined'].includes(b.status)
      );

      timeMarks.forEach(timeMin => {
        const key = `${court.id}|${timeMin}`;
        if (skip.has(key)) { g[key] = { type: 'skip' }; return; }

        // Match any booking overlapping this 30-min cell (handles :15/:45 starts
        // and bookings that begin before the grid's first mark).
        const booking = cBookings.find(b =>
          toMin(b.start_time) < timeMin + 30 && toMin(b.end_time) > timeMin);
        if (booking) {
          const maxSpan = (timeMarks[timeMarks.length - 1] - timeMin) / 30 + 1;
          const rowSpan = Math.min(Math.max(1, Math.ceil((toMin(booking.end_time) - timeMin) / 30)), maxSpan);
          for (let r = 1; r < rowSpan; r++) skip.add(`${court.id}|${timeMin + r * 30}`);
          g[key] = { type: 'booking', booking, rowSpan };
        } else if (isCourtOpenAt(court, timeMin)) {
          g[key] = { type: 'available' };
        } else {
          g[key] = { type: 'closed' };
        }
      });
    });
    return g;
  }, [activeCourts, bookings, timeMarks]);

  /* ── Cell renderers ── */
  const ROW_H_HOUR = 48;  // px for :00 rows
  const ROW_H_HALF = 30;  // px for :30 rows

  const cellBase = (isHour, firstRow) => ({
    borderTop:   isHour && !firstRow ? '1px solid #1E1E1E' : '1px solid #131313',
    borderRight: '1px solid #1A1A1A',
    height:      isHour ? ROW_H_HOUR : ROW_H_HALF,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px 40px' }}>

        {/* Header */}
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 3, color: '#333', marginBottom: 4 }}>Overview</p>
        <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, textTransform: 'uppercase', color: '#fff', marginBottom: 16, lineHeight: 1 }}>
          Court Calendar
        </h1>

        {/* Date navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <button onClick={() => changeDate(-1)} data-testid="sports-monitoring-prev-day"
            style={{ background: '#141414', border: '1px solid #1E1E1E', color: '#A3A3A3', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', lineHeight: 1, flexShrink: 0 }}>
            <ChevronLeft style={{ width: 16, height: 16 }} />
          </button>

          <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button
              data-testid="sports-monitoring-date-picker"
              onClick={() => setShowCal(c => !c)}
              style={{
                background: '#141414', border: '1px solid #1E1E1E', borderRadius: 8,
                padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                color: '#fff', outline: 'none',
              }}>
              <Calendar style={{ width: 14, height: 14, color: '#E63946', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.2 }}>
                  {fmtDateLong(date)}
                </p>
                {date === todayInTz && (
                  <span style={{ fontSize: 9, color: '#E63946', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Today</span>
                )}
                {date !== todayInTz && date === businessToday && (
                  <span data-testid="sports-monitoring-overnight-badge" style={{ fontSize: 9, color: '#FFB300', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Tonight · overnight</span>
                )}
              </div>
            </button>
            {showCal && <MonitoringCalendar value={date} onChange={setDate} onClose={() => setShowCal(false)} todayStr={todayInTz} />}
          </div>

          <button onClick={() => changeDate(1)} data-testid="sports-monitoring-next-day"
            style={{ background: '#141414', border: '1px solid #1E1E1E', color: '#A3A3A3', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', lineHeight: 1, flexShrink: 0 }}>
            <ChevronRight style={{ width: 16, height: 16 }} />
          </button>

          {date !== businessToday && (
            <button onClick={() => setDate(businessToday)}
              data-testid="sports-monitoring-today-btn"
              style={{ background: '#E6394618', border: '1px solid #E6394640', color: '#E63946', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
              Today
            </button>
          )}
        </div>

        {/* Empty state */}
        {activeCourts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0', border: '1px dashed #1A1A1A', borderRadius: 12, color: '#333' }}>
            No active courts yet.
          </div>
        )}

        {/* Grid */}
        {activeCourts.length > 0 && (
          <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #1A1A1A', background: '#0D0D0D', position: 'relative' }}>
            {loading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,10,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 12 }}>
                <div className="animate-spin" style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #E63946', borderTopColor: 'transparent' }} />
              </div>
            )}

            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: `${56 + activeCourts.length * 160}px` }}>
              {/* Column headers */}
              <thead>
                <tr>
                  {/* Time col header */}
                  <th style={{ width: 56, background: '#0A0A0A', borderBottom: '2px solid #1A1A1A', borderRight: '1px solid #1A1A1A', padding: '10px 6px', position: 'sticky', left: 0, zIndex: 4, boxShadow: '2px 0 6px rgba(0,0,0,0.4)' }}>
                    <span style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 1, color: '#333' }}>Time</span>
                  </th>
                  {activeCourts.map(court => (
                    <th key={court.id} data-testid={`sports-monitoring-court-${court.id}`}
                      style={{ background: '#0A0A0A', borderBottom: '2px solid #1A1A1A', borderRight: '1px solid #1A1A1A', padding: '10px 12px', textAlign: 'center', minWidth: 150 }}>
                      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, color: '#fff', marginBottom: 2 }}>
                        {court.name}
                      </p>
                      <p style={{ fontSize: 9, color: '#444', textTransform: 'capitalize', letterSpacing: 0.5 }}>
                        {court.sport} · {fmt12(court.open_time)}–{fmt12(court.close_time)}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {timeMarks.map((timeMin, rowIdx) => {
                  const isHour = timeMin % 60 === 0;
                  const isFirst = rowIdx === 0;

                  return (
                    <tr key={timeMin}>
                      {/* Time label — sticky left */}
                      <td style={{
                        ...cellBase(isHour, isFirst),
                        width: 56, background: '#0A0A0A',
                        borderRight: '1px solid #1A1A1A',
                        position: 'sticky', left: 0, zIndex: 2,
                        boxShadow: '2px 0 6px rgba(0,0,0,0.3)',
                        padding: isHour ? '5px 6px 0' : '3px 6px 0',
                        verticalAlign: 'top',
                      }}>
                        <span style={{
                          fontSize: isHour ? 11 : 9,
                          fontWeight: isHour ? 700 : 400,
                          color: isHour ? '#555' : '#2A2A2A',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                        }}>
                          {fmtTimeMark(timeMin)}
                        </span>
                      </td>

                      {/* Court cells */}
                      {activeCourts.map(court => {
                        const key = `${court.id}|${timeMin}`;
                        const cell = grid[key];
                        if (!cell || cell.type === 'skip') return null;

                        /* ── Booking cell ── */
                        if (cell.type === 'booking') {
                          const b   = cell.booking;
                          const col = bookingColor(b);
                          const isFloat = b.status === 'floating';
                          const name = b.customer?.name || b.event_name || '—';
                          const isCompetition = b.booking_type === 'competition';
                          return (
                            <td key={court.id}
                              rowSpan={cell.rowSpan}
                              data-testid={`sports-monitoring-booking-${b.id}`}
                              onClick={() => setSelected(b)}
                              style={{
                                ...cellBase(isHour, isFirst),
                                background: col + (isFloat ? '14' : '22'),
                                borderLeft: `3px solid ${col}`,
                                opacity: isFloat ? 0.55 : 1,
                                cursor: 'pointer',
                                padding: '7px 10px',
                                verticalAlign: 'middle',
                                transition: 'opacity 0.15s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                              onMouseLeave={e => e.currentTarget.style.opacity = isFloat ? '0.55' : '1'}
                              title={`${name} · ${fmt12(b.start_time)}–${fmt12(b.end_time)}`}>
                              <p style={{ fontSize: 12, fontWeight: 900, color: col, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 2 }}>
                                {isCompetition ? '🏆 ' : ''}{name}
                              </p>
                              <p style={{ fontSize: 9, color: col + 'AA', whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
                                {fmt12(b.start_time)} – {fmt12(b.end_time)}
                              </p>
                              {b.amount > 0 && (
                                <p style={{ fontSize: 9, color: col + '88', marginTop: 1 }}>
                                  ₱{b.amount.toLocaleString()}
                                </p>
                              )}
                            </td>
                          );
                        }

                        /* ── Available cell ── */
                        if (cell.type === 'available') {
                          return (
                            <td key={court.id}
                              style={{ ...cellBase(isHour, isFirst), background: isHour ? '#0D0D0D' : '#0B0B0B' }} />
                          );
                        }

                        /* ── Closed cell ── */
                        return (
                          <td key={court.id}
                            style={{ ...cellBase(isHour, isFirst), background: '#080808' }} />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 14 }}>
          {[
            ['confirmed', 'Confirmed'], ['awaiting_confirmation', 'Pending'],
            ['walk_in', 'Walk-in'], ['competition', 'Competition'],
            ['floating', 'Floating'], ['hold', 'Hold'],
          ].map(([k, label]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#555' }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: STATUS_COLORS[k] + '40', borderLeft: `3px solid ${STATUS_COLORS[k]}` }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Booking detail modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}
          onClick={() => setSelected(null)}>
          <div style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: 16, padding: 22, maxWidth: 420, width: '100%', maxHeight: '88vh', overflowY: 'auto', position: 'relative' }}
            onClick={e => e.stopPropagation()}>

            {/* Colored accent bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: bookingColor(selected) }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, paddingTop: 2, gap: 10 }}>
              <div>
                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, textTransform: 'uppercase', color: '#fff', lineHeight: 1.1 }}>
                  {selected.customer?.name || selected.event_name || '—'}
                </p>
                <p style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, color: bookingColor(selected), marginTop: 2 }}>
                  Booking Details
                </p>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0 }}>×</button>
            </div>

            <BookingDetailBody booking={selected} />
          </div>
        </div>
      )}
    </div>
  );
}
